import React, { useEffect, useState } from "react";
import { Alert, FlatList, StyleSheet, View } from "react-native";
import * as Sentry from "@sentry/react-native";

import NoFollowers from "./NoFollowers";
import NoFollowings from "./NoFollowings";

import ScreenLoadingIcon from "@/Components/Icons/Loading/ScreenLoadingIcon";
import { type AppUserSession } from "@/schemas/auth/session";
import {
  RelationshipType,
  type UserRelationship,
} from "@/schemas/user/relationship";
import RelationshipService from "@/services/user/relationship";
import UserListItem from "@/Components/Lists/UserListItem";

type Props = {
  session: AppUserSession;
  username: string;
  relationshipType: RelationshipType;
};

function RelationshipsScreen({
  session,
  username,
  relationshipType,
}: Props): React.JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [relationships, setRelationships] = useState<UserRelationship[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchMoreRelationships = async (): Promise<void> => {
    if (!nextCursor) return;

    try {
      setIsLoadingMore(true);
      const connectionService = new RelationshipService(session);
      const response = await connectionService.getRelationships(
        username,
        relationshipType,
        { limit: 10, next: nextCursor },
      );
      setRelationships([...relationships, ...response.items]);
      setNextCursor(response.next);
    } catch (error) {
      Sentry.captureException(error);
      Alert.alert("Something went wrong", "Please try again later.", [
        { text: "OK" },
      ]);
    } finally {
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    const fetchRelationships = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const connectionService = new RelationshipService(session);
        const response = await connectionService.getRelationships(
          username,
          relationshipType,
          { limit: 10, next: null },
        );
        setRelationships(response.items);
        setNextCursor(response.next);
      } catch (error) {
        Sentry.captureException(error);
        Alert.alert("Something went wrong", "Please try again later.", [
          { text: "OK" },
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRelationships();
  }, [relationshipType, session, username]);

  const renderItem = ({
    item,
  }: {
    item: UserRelationship;
  }): React.JSX.Element => {
    return <UserListItem session={session} user={item} />;
  };

  return (
    <View style={styles.container}>
      {isLoading ? (
        <ScreenLoadingIcon />
      ) : relationships.length > 0 ? (
        <FlatList
          data={relationships}
          renderItem={(item) => renderItem(item)}
          keyExtractor={(item) => item.id}
          onEndReached={fetchMoreRelationships}
          ListFooterComponent={isLoadingMore ? <ScreenLoadingIcon /> : null}
        />
      ) : relationshipType === RelationshipType.FOLLOWER ? (
        <NoFollowers />
      ) : (
        <NoFollowings />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: "white",
    flex: 1,
  },
});

export default RelationshipsScreen;
